import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Messages from "@/components/Messages";
import { AdminSupport } from "@/components/AdminSupport";
import { Overline } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, FileText, ShieldAlert, SlidersHorizontal, MessageSquare, Building2, LifeBuoy, History, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { UsersTab } from "@/pages/admin/UsersTab";
import { ListingsTab } from "@/pages/admin/ListingsTab";
import { ContentTab } from "@/pages/admin/ContentTab";
import { PlatformTab } from "@/pages/admin/PlatformTab";
import { AuditTab } from "@/pages/admin/AuditTab";
import { AnnouncementsTab } from "@/pages/admin/AnnouncementsTab";
import { AdminDialogs } from "@/pages/admin/AdminDialogs";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delUser, setDelUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const [schema, setSchema] = useState([]);
  const [values, setValues] = useState({});
  const [pfields, setPfields] = useState([]);
  const [pvalues, setPvalues] = useState({});
  const [whTest, setWhTest] = useState(null);
  const [whBusy, setWhBusy] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponType, setCouponType] = useState("landlord_free_month");
  const [couponEmail, setCouponEmail] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [lastCoupon, setLastCoupon] = useState(null);
  const [adminProps, setAdminProps] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "users");

  const loadUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  const loadSupportUnread = () => api.get("/support/unread-count").then((r) => setSupportUnread(r.data.count)).catch(() => {});

  const loadUsers = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadProps = () => {
    api.get("/admin/properties").then((r) => setAdminProps(r.data)).catch(() => {});
  };
  const toggleVisibility = async (p) => {
    try {
      await api.patch(`/properties/${p.id}/visibility`, { hidden: !p.hidden });
      toast.success(!p.hidden ? "Listing hidden from Browse" : "Listing is public again");
      loadProps();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update visibility");
    }
  };
  const deleteListing = async (p) => {
    const extra = p.has_signed_lease ? " This listing has a SIGNED lease — deleting it will also remove that lease and the tenant's rent records." : "";
    if (!window.confirm(`Delete "${p.title}"? This permanently removes the listing and all of its applications, showings, leases, maintenance requests and expense records.${extra} This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`);
      setAdminProps((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Listing deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the listing");
    }
  };
  const loadContent = () => {
    api.get("/admin/content/schema").then((r) => { setSchema(r.data.schema); setValues(r.data.values); }).catch(() => {});
  };
  const loadPlatform = () => {
    api.get("/admin/platform-settings").then((r) => {
      setPfields(r.data.fields);
      const v = {};
      r.data.fields.forEach((f) => { v[f.key] = f.secret ? "" : (f.display || ""); });
      setPvalues(v);
    }).catch(() => {});
  };
  const loadCoupons = () => {
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  };
  const loadAudit = () => {
    api.get("/admin/audit-logs").then((r) => setAuditLogs(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadUsers();
    loadContent();
    loadPlatform();
    loadProps();
    loadUnread();
    loadSupportUnread();
    loadCoupons();
    const id = setInterval(() => { loadUnread(); loadSupportUnread(); }, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === "audit") loadAudit();
  }, [activeTab]);

  const savePlatform = async () => {
    setBusy(true);
    try {
      const items = {};
      Object.entries(pvalues).forEach(([k, val]) => { if (val !== "" && !String(val).startsWith("••••")) items[k] = val; });
      await api.put("/admin/platform-settings", { items });
      toast.success("Platform settings saved");
      loadPlatform();
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const sendTestWebhook = async () => {
    setWhBusy(true);
    setWhTest(null);
    try {
      const { data } = await api.post("/admin/test-webhook", { origin_url: window.location.origin });
      setWhTest(data);
      if (data.ok) toast.success("Webhook endpoint responded OK");
      else toast.error(data.message || "Webhook test failed");
    } catch (e) {
      setWhTest({ ok: false, message: e.response?.data?.detail || "Could not run the webhook test." });
      toast.error("Could not run the webhook test");
    } finally {
      setWhBusy(false);
    }
  };

  const generateCoupon = async () => {
    setCouponBusy(true);
    setLastCoupon(null);
    try {
      const { data } = await api.post("/admin/coupons", { type: couponType, recipient_email: couponEmail.trim() });
      setLastCoupon(data);
      toast.success(data.emailed ? `Coupon generated and emailed to ${couponEmail.trim()}` : "Coupon generated");
      setCouponEmail("");
      loadCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate coupon");
    } finally {
      setCouponBusy(false);
    }
  };

  const toggleCoupon = async (c) => {
    try {
      await api.post(`/admin/coupons/${c.id}/status`);
      loadCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update coupon");
    }
  };

  const deleteCoupon = async (c) => {
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Coupon deleted");
      loadCoupons();
    } catch (e) {
      toast.error("Could not delete coupon");
    }
  };

  const setPasswordDirect = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${pwUser.id}/set-password`, { password: newPw });
      toast.success(`Password updated for ${pwUser.email}`);
      setPwUser(null);
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set password");
    } finally {
      setBusy(false);
    }
  };

  const sendResetLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${pwUser.id}/send-reset`, { origin_url: window.location.origin });
      toast.success(data.emailed ? "Reset link emailed to user" : "Reset link generated");
      navigator.clipboard?.writeText(data.reset_link).catch(() => {});
      toast.message("Reset link copied to clipboard");
      setPwUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reset");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${delUser.id}`);
      toast.success(`Deleted ${delUser.email}`);
      setDelUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete user");
    } finally {
      setBusy(false);
    }
  };

  const toggleMaster = async (u) => {
    const enable = !u.master_landlord;
    try {
      await api.post(`/admin/users/${u.id}/master`, { enabled: enable });
      toast.success(enable ? `${u.name} is now a Master Landlord (Pro, 0% fees)` : `Removed Master access from ${u.name}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Master status");
    }
  };

  const onImage = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image over 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setValues((v) => ({ ...v, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveContent = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { items: values });
      toast.success("Content saved — changes are live");
      loadContent();
    } catch (e) {
      toast.error("Could not save content");
    } finally {
      setBusy(false);
    }
  };

  const resetContent = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/content/reset");
      setValues(data);
      toast.success("Content reset to defaults");
    } catch (e) {
      toast.error("Could not reset");
    } finally {
      setBusy(false);
    }
  };

  const tabTrigger = "shrink-0 data-[state=active]:bg-forest data-[state=active]:text-white rounded-lg px-4 py-2 text-sm gap-2";

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-forest" />
          <Overline>Master admin</Overline>
        </div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink">Control Center</h1>
        <p className="text-sm text-mute mt-1">Manage users and edit site content across all pages.</p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="bg-white border border-line rounded-xl p-1 h-auto flex flex-nowrap overflow-x-auto no-scrollbar md:flex-wrap justify-start gap-1">
            <TabsTrigger value="users" data-testid="tab-admin-users" className={tabTrigger}><Users className="h-4 w-4" /> Users</TabsTrigger>
            <TabsTrigger value="listings" data-testid="tab-admin-listings" className={tabTrigger}><Building2 className="h-4 w-4" /> Listings</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-admin-messages" className={tabTrigger}><MessageSquare className="h-4 w-4" /> Messages{unread > 0 && <span data-testid="admin-messages-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{unread}</span>}</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-admin-support" className={tabTrigger}><LifeBuoy className="h-4 w-4" /> Support{supportUnread > 0 && <span data-testid="admin-support-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{supportUnread}</span>}</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-admin-announcements" className={tabTrigger}><Megaphone className="h-4 w-4" /> Announcements</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-admin-content" className={tabTrigger}><FileText className="h-4 w-4" /> Site Content</TabsTrigger>
            <TabsTrigger value="platform" data-testid="tab-admin-platform" className={tabTrigger}><SlidersHorizontal className="h-4 w-4" /> Platform Settings</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-admin-audit" className={tabTrigger}><History className="h-4 w-4" /> Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <UsersTab users={users} loading={loading} onResetPw={setPwUser} onDelete={setDelUser} onToggleMaster={toggleMaster} />
          </TabsContent>

          <TabsContent value="listings" className="mt-6">
            <ListingsTab adminProps={adminProps} onToggleVisibility={toggleVisibility} onDelete={deleteListing} />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <Messages onActivity={loadUnread} />
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <AdminSupport onActivity={loadSupportUnread} />
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <ContentTab schema={schema} values={values} setValues={setValues} busy={busy} onSave={saveContent} onReset={resetContent} onImage={onImage} />
          </TabsContent>

          <TabsContent value="platform" className="mt-6">
            <PlatformTab
              pfields={pfields} pvalues={pvalues} setPvalues={setPvalues} busy={busy} onSave={savePlatform}
              whTest={whTest} whBusy={whBusy} onSendTestWebhook={sendTestWebhook}
              coupons={coupons} couponType={couponType} setCouponType={setCouponType}
              couponEmail={couponEmail} setCouponEmail={setCouponEmail}
              couponBusy={couponBusy} onGenerateCoupon={generateCoupon} lastCoupon={lastCoupon}
              onToggleCoupon={toggleCoupon} onDeleteCoupon={deleteCoupon}
            />
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <AuditTab auditLogs={auditLogs} onRefresh={loadAudit} />
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <AnnouncementsTab users={users} />
          </TabsContent>
        </Tabs>
      </div>

      <AdminDialogs
        pwUser={pwUser} setPwUser={setPwUser} newPw={newPw} setNewPw={setNewPw}
        busy={busy} onSetPassword={setPasswordDirect} onSendReset={sendResetLink}
        delUser={delUser} setDelUser={setDelUser} onDeleteUser={deleteUser}
      />
    </div>
  );
}
